import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Typography,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api from "./api/api";
import { ENDPOINTS } from "./api/endpoints";
import { useTheme, useMediaQuery } from "@mui/material";
import UploadMediaDialog from "./UploadMediaDialog";

/* ------------------ Video Helpers ------------------ */
const getYouTubeEmbed = (url) => {
  if (!url) return null;
  if (url.includes("/embed/")) return url;

  const match = url.match(/(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
};

const getVimeoEmbed = (url) => {
  const match = url?.match(/vimeo\.com\/(\d+)/);
  return match ? `https://player.vimeo.com/video/${match[1]}` : null;
};

/* ------------------ Sortable Item ------------------ */
function SortableMediaItem({ media, onDelete, onTitleChange, isReordering }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: media.mediaId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    border: "1px solid #ccc",
    padding: 12,
    borderRadius: 6,
    marginBottom: 12,
    background: "#fafafa",
    cursor: isReordering ? "grab" : "default",
    touchAction: isReordering ? "none" : "auto",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isReordering ? listeners : {})}
    >
      <Grid container spacing={2} alignItems="center">
        {/* 🎬 MEDIA PREVIEW */}
        <Grid item xs={12} sm={4} {...(isReordering ? listeners : {})}>
          {media.mediaType === "PICTURE" && (
            <img
              src={media.mediaName}
              alt="Media"
              style={{
                width: "100%",
                maxHeight: 160,
                objectFit: "cover",
                borderRadius: 6,
              }}
            />
          )}

          {media.mediaType === "AUDIO" && (
            <audio
              controls
              style={{
                width: "100%",
                pointerEvents: isReordering ? "none" : "auto",
              }}
            >
              <source src={media.mediaName} />
            </audio>
          )}

          {media.mediaType === "VIDEO" && (
            <video
              controls
              style={{
                width: "100%",
                maxHeight: 160,
                borderRadius: 6,
                pointerEvents: isReordering ? "none" : "auto",
              }}
            >
              <source src={media.mediaName} />
            </video>
          )}

          {media.mediaType === "YTvideo" &&
            getYouTubeEmbed(media.mediaName) && (
              <iframe
                src={getYouTubeEmbed(media.mediaName)}
                style={{
                  width: "100%",
                  height: 160,
                  borderRadius: 6,
                  pointerEvents: isReordering ? "none" : "auto",
                }}
                frameBorder="0"
                allowFullScreen
              />
            )}

          {media.mediaType === "VIMvideo" && getVimeoEmbed(media.mediaName) && (
            <iframe
              src={getVimeoEmbed(media.mediaName)}
              style={{
                width: "100%",
                height: 160,
                borderRadius: 6,
                pointerEvents: isReordering ? "none" : "auto",
              }}
              frameBorder="0"
              allowFullScreen
            />
          )}
        </Grid>

        {/*  TITLE */}
        <Grid item xs={12} sm={6}>
          {isReordering ? (
            <Typography variant="body2">
              {media.mediaTitle || media.mediaName}
            </Typography>
          ) : (
            <input
              type="text"
              defaultValue={media.mediaTitle || ""}
              placeholder="Enter title"
              disabled={media.mediaType === "PICTURE"}
              style={{
                width: "100%",
                padding: "6px 8px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 14,
                backgroundColor:
                  media.mediaType === "PICTURE" ? "#f5f5f5" : "white",
                cursor: media.mediaType === "PICTURE" ? "not-allowed" : "text",
              }}
              onBlur={(e) => {
                if (media.mediaType === "PICTURE") return;

                const newTitle = e.target.value.trim();
                if (newTitle !== (media.mediaTitle || "")) {
                  onTitleChange(media, newTitle);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.target.blur();
              }}
            />
          )}
        </Grid>

        {/* 🗑 DELETE */}
        <Grid
          item
          xs={12}
          sm={2}
          sx={{
            textAlign: { xs: "right", sm: "center" },
          }}
        >
          {!isReordering && (
            <IconButton onClick={() => onDelete(media)}>
              <DeleteIcon color="error" />
            </IconButton>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

/* ------------------ Main Component ------------------ */
export default function MediaEditDialog({ open, onClose, userId }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReordering, setIsReordering] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [tab, setTab] = useState(0);
  const [uploadMediaOpen, setUploadMediaOpen] = useState(false);

  /* -------- Fetch Media -------- */
  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await api.get(ENDPOINTS.getUserMedia({ userId }));
      const result = Array.isArray(res.data) ? res.data : [];

      const active = result.filter((m) => m.activeFlag === 1);

      const sorted = active
        .map((m, i) => ({
          ...m,
          picSort: typeof m.picSort === "number" ? m.picSort : i,
        }))
        .sort((a, b) => a.picSort - b.picSort);

      setItems(sorted);
    } catch (err) {
      console.error("Error fetching media:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchMedia();
  }, [open]);

  /* -------- Media Type Filters -------- */
  const getCurrentMediaTypes = () => {
    if (tab === 0) return ["PICTURE"];
    if (tab === 1) return ["AUDIO"];
    if (tab === 2) return ["VIDEO", "YTvideo", "VIMvideo"];
    return [];
  };

  const filteredItems = () =>
    items.filter((m) => getCurrentMediaTypes().includes(m.mediaType));

  /* -------- Save Order -------- */
  const updatePicSort = async (updatedItems) => {
    setIsSavingOrder(true);

    const requests = updatedItems.map((m, i) => {
      const formData = new FormData();
      formData.append("MediaId", m.mediaId);
      formData.append("UserId", userId);
      formData.append("MediaType", m.mediaType);
      formData.append("MediaTitle", m.mediaTitle || "");
      formData.append("PicSort", i);
      formData.append("ActiveFlag", true);
      formData.append("UserBy", userId);
      formData.append("MediaFile", "NULL");

      return api.post(ENDPOINTS.addOrUpdateUserMedia(), formData);
    });

    await Promise.all(requests);
    setIsSavingOrder(false);
  };

  /* -------- Drag End -------- */
  const handleDragEnd = async (event) => {
    if (!isReordering || isSavingOrder) return;

    const { active, over } = event;
    if (!active || !over) return;

    const current = filteredItems();
    const oldIndex = current.findIndex((i) => i.mediaId === active.id);
    const newIndex = current.findIndex((i) => i.mediaId === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(current, oldIndex, newIndex);

    const updatedItems = [...items];
    const indexes = items.reduce((acc, cur, idx) => {
      if (getCurrentMediaTypes().includes(cur.mediaType)) acc.push(idx);
      return acc;
    }, []);

    indexes.forEach((originalIndex, i) => {
      updatedItems[originalIndex] = reordered[i];
    });

    setItems(updatedItems);
    await updatePicSort(updatedItems);
    await fetchMedia();

    // 🔁 Refresh TalentHome
    window.dispatchEvent(new Event("talent-data-updated"));
  };

  /* -------- Delete -------- */
  const handleDelete = async (mediaItem) => {
    const formData = new FormData();
    formData.append("MediaId", mediaItem.mediaId);
    formData.append("UserId", userId);
    formData.append("MediaType", mediaItem.mediaType);
    formData.append("ActiveFlag", false);
    formData.append("UserBy", userId);
    formData.append("MediaFile", new Blob([]));

    try {
      await api.post(ENDPOINTS.addOrUpdateUserMedia(), formData);
      await fetchMedia();

      //  Refresh TalentHome
      window.dispatchEvent(new Event("talent-data-updated"));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };
  const handleTitleChange = async (mediaItem, newTitle) => {
    const formData = new FormData();
    formData.append("MediaId", mediaItem.mediaId);
    formData.append("UserId", userId);
    formData.append("MediaType", mediaItem.mediaType);
    formData.append("MediaTitle", newTitle);
    formData.append("PicSort", mediaItem.picSort || 0);
    formData.append("ActiveFlag", true);
    formData.append("UserBy", userId);
    formData.append("MediaFile", "NULL");

    try {
      await api.post(ENDPOINTS.addOrUpdateUserMedia(), formData);
      await fetchMedia();

      window.dispatchEvent(new Event("talent-data-updated"));
    } catch (err) {
      console.error("Title update error:", err);
    }
  };

  /* -------- Close -------- */
  const handleClose = () => {
    setIsReordering(false);
    window.dispatchEvent(new Event("talent-data-updated"));
    onClose();
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // prevents accidental drag
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <DialogTitle>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    }}
  >
    <span>Manage Media</span>

    <Button
  variant="outlined"
  size="small"
  onClick={() => setUploadMediaOpen(true)}
  sx={{
    textTransform: "none",
    whiteSpace: "nowrap"
  }}
>
  UPLOAD MEDIA
</Button>
  </Box>
</DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Tabs value={tab} onChange={(_, newTab) => setTab(newTab)}>
            <Tab label="Images" />
            <Tab label="Audio" />
            <Tab label="Video" />
          </Tabs>

          {filteredItems().length > 0 && (
            <Button
              onClick={() => setIsReordering((prev) => !prev)}
              variant="contained"
              color="secondary"
              disabled={isSavingOrder}
              fullWidth
              sx={{ width: { sm: "auto" } }}
            >
              {isReordering ? "Done Reordering" : "Reorder Media"}
            </Button>
          )}
        </Box>

        {isReordering && (
          <Box
            sx={{
              backgroundColor: "#f3f6fb",
              border: "1px solid #dce3f1",
              p: 1.5,
              mt: 2,
              mb: 2,
              borderRadius: 1,
              fontSize: 13,
              color: "#2c3e50",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {isSavingOrder ? (
              <>
                <CircularProgress size={18} />
                <strong>Saving new order...</strong>
              </>
            ) : (
              <>
                <strong>Reorder Mode Enabled</strong>
                <span>
                  Drag media to change their order. Click <b>Done Reordering</b>
                  .
                </span>
              </>
            )}
          </Box>
        )}

        <Box mt={2}>
          {loading ? (
            <CircularProgress />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredItems().map((m) => m.mediaId)}
                strategy={verticalListSortingStrategy}
              >
                {filteredItems().map((media) => (
                  <SortableMediaItem
                    key={media.mediaId}
                    media={media}
                    onDelete={handleDelete}
                    onTitleChange={handleTitleChange}
                    isReordering={isReordering}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSavingOrder}>
          Close
        </Button>
      </DialogActions>
      <UploadMediaDialog
  open={uploadMediaOpen}
  onClose={() => setUploadMediaOpen(false)}
  userId={userId}
  onUploadSuccess={() => {
    fetchMedia();
    window.dispatchEvent(new Event("talent-data-updated"));
  }}
/>
    </Dialog>
    
  );
}
